import React from 'react';

import { Button, Checkbox, Flex, Table } from 'antd';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from 'segments/hooks';
import { defaultPaginationConfig } from 'segments/utils/constants/common';

import { changeActiveModal } from 'features/modals';
import { Role, rolesTitles, useTimezone } from 'features/user';
import { convertUsersToTable, UserActions, UserInfo, useUserBanEdit } from 'features/users';

export interface TableData extends UserInfo {
  key: number;
}

type Props = {
  items: UserInfo[];
  itemsTotal?: number;
  isLoading?: boolean;
  currentPage?: number;
  defaultPageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
};

export const UsersTable = (props: Props) => {
  const {
    items,
    itemsTotal = 0,
    isLoading,
    currentPage = 1,
    defaultPageSize = 30,
    onPageChange,
  } = props;

  const dispatch = useAppDispatch();
  const { banUser, banLoading } = useUserBanEdit();
  const { timezone } = useTimezone();
  const { t } = useTranslation(undefined, { keyPrefix: 'table' });
  const handleCreateUser = () => {
    dispatch(changeActiveModal('create-user'));
  };

  return (
    <Table<TableData>
      size="small"
      dataSource={convertUsersToTable(timezone, items)}
      loading={isLoading}
      scroll={{ x: 1200 }}
      pagination={{
        ...defaultPaginationConfig,
        defaultPageSize,
        current: currentPage,
        total: itemsTotal,
        onChange: onPageChange,
      }}
    >
      <Table.Column<TableData>
        title={t('id', 'ID')}
        dataIndex="id"
        width={100}
      />
      <Table.Column<TableData>
        title={t('balance', 'баланс')}
        dataIndex="balance"
        width={120}
        render={(balance: number) => `${balance}₽`}
      />
      <Table.Column<TableData>
        title={t('contacts', 'Контакты')}
        dataIndex="email"
        render={(_, { phone, email }) => (
          <>
            {email && <p>{email}</p>}
            {phone && <p>{phone}</p>}
          </>
        )}
      />
      <Table.Column<TableData>
        title={t('fio', 'ФИО')}
        dataIndex="name"
      />
      <Table.Column<TableData>
        title={t('register', 'Дата регистрации')}
        dataIndex="created"
        width={240}
      />
      <Table.Column<TableData>
        title={t('role', 'Роль')}
        dataIndex="role"
        render={(role: Role) => rolesTitles[role]}
        width={170}
      />
      <Table.Column<TableData>
        title={t('ban', 'Бан')}
        dataIndex="isBanned"
        width={80}
        render={(isBanned: boolean, { id }) => (
          <Checkbox
            disabled={banLoading}
            checked={isBanned}
            onClick={() => {
              banUser(id, isBanned);
            }}
          />
        )}
      />
      <Table.Column<TableData>
        dataIndex=""
        fixed="right"
        title={() => (
          <Flex justify='flex-end'>
            <Button type='primary' onClick={handleCreateUser}>{t('add-user')}</Button>
          </Flex >
        )}
        render={(_, user) => (
          <Flex justify='flex-end'>
            <UserActions
              user={user}
            />
          </Flex>
        )}
      />
    </Table>
  );
};
